import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeTextArea } from '@/components/ui/tee-textarea';
import { usePlaylists } from '@/hooks/usePlaylists';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';
import { Playlist, PlaylistFormData } from '@/types/playlist';

interface PlaylistFormModalProps {
  isOpen: boolean;
  editTarget: Playlist | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PlaylistFormModal({
  isOpen,
  editTarget,
  onClose,
  onSaved,
}: PlaylistFormModalProps) {
  const { t } = useTranslation();
  const { createPlaylist, updatePlaylist } = usePlaylists();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prefill from editTarget (rename mode, opened from PlaylistBuilderPage's
  // header — Plan 07) or reset to blank (create mode, opened from
  // PlaylistsPanel — Task 2 in this plan).
  useEffect(() => {
    if (editTarget) {
      setName(editTarget.name ?? '');
      setDescription(editTarget.description ?? '');
    } else {
      setName('');
      setDescription('');
    }
    setNameError(null);
  }, [editTarget, isOpen]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(t('contentLibrary.fieldTitle'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Only name/description ever flow through this modal — item membership
      // is owned by the Playlist Builder's picker (D-07), and
      // status/publishedAt/createdBy/updatedBy are server-owned lifecycle
      // and audit fields never accepted by CreatePlaylistDto/UpdatePlaylistDto.
      const data: PlaylistFormData = {
        name: trimmedName,
        description: description.trim() || undefined,
      };

      if (editTarget) {
        await updatePlaylist(editTarget.id, data);
      } else {
        await createPlaylist(data);
      }

      showSuccessToast(
        t(editTarget ? 'contentLibrary.playlistSaved' : 'contentLibrary.playlistCreated')
      );
      onSaved();
      onClose();
    } catch {
      showErrorToast(t('contentLibrary.errorSavePlaylist'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TeeFormModal
      isOpen={isOpen}
      title={t(editTarget ? 'contentLibrary.modalTitleEditPlaylist' : 'contentLibrary.modalTitleCreatePlaylist')}
      onClose={onClose}
      onPrimaryAction={handleSave}
      isLoading={isSubmitting}
      primaryActionLabel={t(editTarget ? 'contentLibrary.saveChanges' : 'contentLibrary.createPlaylist')}
      secondaryActionLabel={t('common.cancel')}
    >
      <div className="flex flex-col gap-4">
        <TeeInput
          label={t('contentLibrary.playlistName')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          destructive={!!nameError}
          hint={nameError ?? undefined}
        />

        <TeeTextArea
          label={t('contentLibrary.playlistDescription')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>
    </TeeFormModal>
  );
}
